import { AlertTriangle } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';

// Local copy of the global FailedQuestionInfo from types.d.ts. Inlined
// here because tsc's isolatedModules treatment of ambient-global
// interfaces from .d.ts files is inconsistent across IDE typechecks
// and Vite's compile pass — so we duplicate the shape (small, stable)
// to avoid mysterious "Cannot find name" errors during build.
interface FailedQuestionInfo {
  question_id: string;
  question_text: string;
  last_error: string;
  failed_at_phase: 'batch' | 'meta';
  retry_attempted: boolean;
}

interface PartialAnalysisBannerProps {
  failedQuestions: FailedQuestionInfo[];
  totalQuestionCount: number;
}

/**
 * Banner shown on the analysis page when the hierarchical pipeline
 * finished with one or more questions that couldn't be analysed even
 * after the inline retry phase. The OverallPage state is still DONE in
 * this case — the failure is data-level (a specific question's batches
 * or meta synthesis exhausted retries), not a system error. The
 * surviving questions ARE analysed and visible elsewhere on the page.
 *
 * Backend ops (`support@tellet.ai`) is alerted via a separate email
 * pipeline; the user-facing banner is purely informative and points
 * the user at the option to re-run analysis when they're ready.
 *
 * Empty / undefined `failed_questions` should bypass this component
 * entirely (caller's responsibility); we still render `null` defensively.
 */
function PartialAnalysisBanner({
  failedQuestions,
  totalQuestionCount,
}: PartialAnalysisBannerProps) {
  const { lang } = useTranslation();

  if (!failedQuestions || failedQuestions.length === 0) {
    return null;
  }

  const failedCount = failedQuestions.length;
  const allFailed = failedCount === totalQuestionCount;

  return (
    <div
      role="alert"
      className="border border-amber-300 bg-amber-50 rounded-md px-4 py-3 mb-4 flex items-start gap-3"
    >
      <AlertTriangle
        className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-900">
          {allFailed
            ? lang.get('msg.analysisAllQuestionsFailed') ||
              'Analysis could not be completed for any questions.'
            : (lang.get('msg.analysisPartialFailureBanner') ||
                'Analysis is partially complete — {failed} of {total} question(s) could not be analysed.')
                .replace('{failed}', String(failedCount))
                .replace('{total}', String(totalQuestionCount))}
        </p>
        <details className="mt-2">
          <summary className="text-xs text-amber-800 cursor-pointer hover:text-amber-900 select-none">
            {lang.get('msg.analysisPartialFailureShowDetails') ||
              'Show failed questions'}
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-amber-900">
            {failedQuestions.map((fq) => (
              <li key={fq.question_id} className="flex gap-2">
                <span className="font-mono text-amber-700 flex-shrink-0">
                  [{fq.failed_at_phase}]
                </span>
                <span className="break-words">{fq.question_text}</span>
              </li>
            ))}
          </ul>
        </details>
        <p className="text-xs text-amber-800 mt-2">
          {lang.get('msg.analysisPartialFailureRetryHint') ||
            'Our team has been alerted. You can re-run the analysis to retry the failed questions.'}
        </p>
      </div>
    </div>
  );
}

export default PartialAnalysisBanner;
